/**
 * 計算フィールド 計算エンジン（#3145）の関数実装
 * （LEFT / RIGHT / MID / REPLACE / MATCH / ROUND / ROUNDDOWN / ROUNDUP / ASC）。
 * 文字数はコードポイント単位で数える（サロゲートペア対応）。
 * ⚠️ functions/src/utils/calculation と main/src/utils/calculation は同一内容。
 *    片方だけ変更しないこと（変更時は両方へ反映し、共通テストで一致を確認する）。
 */
import {
  CALCULATION_REGEX_MAX_LENGTH,
  CALCULATION_RESULT_SIGNIFICANT_DIGITS,
  CALCULATION_ROUND_DIGITS_LIMIT,
  CALCULATION_TEXT_MAX_LENGTH,
  CalculationFunctionName,
  calculationErrorCode,
} from './constants';
import { CalculationFormulaError } from './calculationError';
import { EvaluatedValue } from './types';
import { toNumericValue, toTextValue } from './conversion';

/** コードポイント単位の文字配列にする（サロゲートペアを1文字と数える） */
const toCodePoints = (text: string): string[] => Array.from(text);

/** 半角にできる全角文字（U+FF01〜U+FF5E の英数字・記号）。#3359 */
const FULL_WIDTH_CHAR_PATTERN = /[！-～]/g;
/** 全角スペース（U+3000）。上の範囲に含まれないため個別に扱う */
const FULL_WIDTH_SPACE_PATTERN = /\u3000/g;
/** 全角から半角へのコードポイントの差（U+FF01 - U+0021） */
const FULL_WIDTH_OFFSET = 0xfee0;

/** 正規表現を安全に構築する。不正なパターンは invalid_regex エラーを投げる */
const buildRegex = (pattern: string, flags: string): RegExp => {
  try {
    return new RegExp(pattern, flags);
  } catch {
    throw new CalculationFormulaError(calculationErrorCode.invalidRegex, `パターン: ${pattern}`);
  }
};

/** 正規表現関数の入力上限チェック（ReDoS 対策。設計書 §9.2） */
const assertRegexLimits = (text: string, pattern: string): void => {
  if (pattern.length > CALCULATION_REGEX_MAX_LENGTH) {
    throw new CalculationFormulaError(
      calculationErrorCode.limitExceeded,
      `正規表現は ${CALCULATION_REGEX_MAX_LENGTH} 文字以内で入力してください`,
    );
  }
  if (text.length > CALCULATION_TEXT_MAX_LENGTH) {
    throw new CalculationFormulaError(
      calculationErrorCode.limitExceeded,
      `計算対象の文字列は ${CALCULATION_TEXT_MAX_LENGTH} 文字以内です`,
    );
  }
};

const applyLeft = (text: EvaluatedValue, count: EvaluatedValue): EvaluatedValue => {
  const numericCount = toNumericValue(count);
  if (numericCount === null) {
    return null;
  }
  const size = Math.floor(numericCount);
  if (size <= 0) {
    return '';
  }
  return toCodePoints(toTextValue(text)).slice(0, size).join('');
};

const applyRight = (text: EvaluatedValue, count: EvaluatedValue): EvaluatedValue => {
  const numericCount = toNumericValue(count);
  if (numericCount === null) {
    return null;
  }
  const size = Math.floor(numericCount);
  if (size <= 0) {
    return '';
  }
  return toCodePoints(toTextValue(text)).slice(-size).join('');
};

const applyMid = (
  text: EvaluatedValue,
  start: EvaluatedValue,
  count: EvaluatedValue,
): EvaluatedValue => {
  const numericStart = toNumericValue(start);
  const numericCount = toNumericValue(count);
  if (numericStart === null || numericCount === null) {
    return null;
  }
  // 開始位置は 1 始まり（kintone・Excel の慣習）。1 未満は 1 として扱う
  const startIndex = Math.max(1, Math.floor(numericStart)) - 1;
  const size = Math.floor(numericCount);
  if (size <= 0) {
    return '';
  }
  return toCodePoints(toTextValue(text))
    .slice(startIndex, startIndex + size)
    .join('');
};

const applyReplace = (
  text: EvaluatedValue,
  pattern: EvaluatedValue,
  replacement: EvaluatedValue,
): EvaluatedValue => {
  const targetText = toTextValue(text);
  const patternText = toTextValue(pattern);
  assertRegexLimits(targetText, patternText);
  // 全件置換（g フラグ相当）。置換文字列では $1 等のキャプチャ参照を使える
  const regex = buildRegex(patternText, 'g');
  return targetText.replace(regex, toTextValue(replacement));
};

/**
 * 全角英数字・記号を半角にする（#3359）。
 *
 * 変換するのは U+FF01〜U+FF5E（全角英数字・記号）と全角スペース（U+3000）のみ。
 * 全角カナは変換しない（Excel の ASC とは意図的に異なる）。
 * 半角カナは CSV 出力先で文字化けの原因になりやすく、社内の正規化（normalizeChoiceText）も
 * 半角カナ → 全角カナの向きに寄せているため、逆向きの変換は提供しない。
 */
const applyAsc = (text: EvaluatedValue): EvaluatedValue => {
  if (text === null) {
    return null;
  }
  return toTextValue(text)
    .replace(FULL_WIDTH_CHAR_PATTERN, char =>
      String.fromCharCode(char.charCodeAt(0) - FULL_WIDTH_OFFSET),
    )
    .replace(FULL_WIDTH_SPACE_PATTERN, ' ');
};

const applyMatch = (text: EvaluatedValue, pattern: EvaluatedValue): EvaluatedValue => {
  const targetText = toTextValue(text);
  const patternText = toTextValue(pattern);
  assertRegexLimits(targetText, patternText);
  const regex = buildRegex(patternText, '');
  const matched = targetText.match(regex);
  return matched ? matched[0] : '';
};

/**
 * 丸めの方向。いずれも 0 から見た絶対値に対して適用する（Excel と同じ挙動）。
 * 例: ROUNDDOWN(-1.5, 0) は -1（Math.floor の -2 ではない）
 */
type RoundingMode = (absoluteValue: number) => number;

/**
 * 桁数を指定して丸める。
 * 桁数は Excel と同じ意味（正: 小数点以下の桁数 / 0: 整数 / 負: 10 の位・100 の位…）。
 *
 * 10 のべき乗を掛けてから丸めるが、その掛け算自体に浮動小数点誤差が乗る
 * （例: 2.675 * 100 = 267.49999999999997）。そのままだと ROUND(2.675, 2) が 2.67 になってしまうため、
 * 値の文字列化と同じ有効桁数で誤差を落としてから丸める。
 */
const roundWithDigits = (
  value: EvaluatedValue,
  digits: EvaluatedValue,
  mode: RoundingMode,
): EvaluatedValue => {
  const numericValue = toNumericValue(value);
  const numericDigits = toNumericValue(digits);
  if (numericValue === null || numericDigits === null) {
    return null;
  }
  const targetDigits = Math.trunc(numericDigits);
  if (Math.abs(targetDigits) > CALCULATION_ROUND_DIGITS_LIMIT) {
    throw new CalculationFormulaError(
      calculationErrorCode.limitExceeded,
      `丸めの桁数は -${CALCULATION_ROUND_DIGITS_LIMIT} 〜 ${CALCULATION_ROUND_DIGITS_LIMIT} で指定してください`,
    );
  }

  const factor = Math.pow(10, targetDigits);
  const scaled = parseFloat(
    (numericValue * factor).toPrecision(CALCULATION_RESULT_SIGNIFICANT_DIGITS),
  );
  const rounded = Math.sign(scaled) * mode(Math.abs(scaled));
  return rounded / factor;
};

/** 関数名に応じて評価済み引数へ関数を適用する */
export const applyCalculationFunction = (
  name: CalculationFunctionName,
  args: EvaluatedValue[],
): EvaluatedValue => {
  switch (name) {
    case 'LEFT':
      return applyLeft(args[0], args[1]);
    case 'RIGHT':
      return applyRight(args[0], args[1]);
    case 'MID':
      return applyMid(args[0], args[1], args[2]);
    case 'REPLACE':
      return applyReplace(args[0], args[1], args[2]);
    case 'MATCH':
      return applyMatch(args[0], args[1]);
    case 'ASC':
      return applyAsc(args[0]);
    case 'IF':
      // IF は採用する枝だけを評価するため evaluator 側で処理する（ここには来ない）
      return null;
    case 'AND':
    case 'OR':
    case 'NOT':
      // 論理演算は短絡評価するため evaluator 側で処理する（ここには来ない）
      return null;
    case 'SUM':
      // SUM は集計コンテキストが要るため evaluator 側で処理する（ここには来ない）
      return null;
    case 'ROUND':
      return roundWithDigits(args[0], args[1], Math.round);
    case 'ROUNDDOWN':
      return roundWithDigits(args[0], args[1], Math.floor);
    case 'ROUNDUP':
      return roundWithDigits(args[0], args[1], Math.ceil);
  }
};
